/**
 * Theme-level accessibility fixes for Shopify Forms, PDP content, and Judge.me widgets.
 */
const initTaxExemptionFormA11y = () => {
  const setupEmbed = (embed) => {
    if (!embed || embed.dataset.a11yFormSetup === 'true') return;
    embed.dataset.a11yFormSetup = 'true';

    const applyFixes = (shadow) => {
      if (!shadow) return false;

      const hasFields = shadow.querySelector('input:not([type="hidden"]), select, textarea');
      if (!hasFields) return false;

      fixShopifyFormFieldLabels(shadow);
      fixShopifyFormFieldErrors(shadow);
      fixShopifyFormFormatValidationErrors(shadow);
      fixShopifyFormAutocomplete(shadow);
      fixShopifyFormFieldBorderContrast(shadow);
      fixShopifyFormFieldLabelContrast(shadow);
      fixShopifyFormFileUpload(shadow);
      fixShopifyFormSubmitButtonFocus(shadow);
      fixShopifyFormSubmitSuccessAnnouncement(shadow);
      return true;
    };

    let shadowObserver;

    const tryApply = () => {
      if (!embed.shadowRoot) return;

      applyFixes(embed.shadowRoot);

      if (!shadowObserver) {
        shadowObserver = new MutationObserver(tryApply);
        shadowObserver.observe(embed.shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            'aria-labelledby',
            'aria-describedby',
            'aria-invalid',
            'aria-label',
            'data-current-step',
            'id',
            'for',
            'class',
            'autocomplete',
          ],
        });
      }
    };

    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      tryApply();
      if (attempts >= 20) {
        window.clearInterval(retry);
      }
    }, 500);

    const embedObserver = new MutationObserver(tryApply);
    embedObserver.observe(embed, { childList: true, subtree: true });

    tryApply();
  };

  const discoverShopifyFormEmbeds = () => {
    document.querySelectorAll('shopify-forms-embed').forEach(setupEmbed);
  };

  discoverShopifyFormEmbeds();

  const pageObserver = new MutationObserver(discoverShopifyFormEmbeds);
  pageObserver.observe(document.body, { childList: true, subtree: true });
};

const fixShopifyFormFieldLabels = (shadow) => {
  shadow.querySelectorAll('input:not([type="hidden"]):not([type="file"]), select, textarea').forEach((input) => {
    const label =
      input.labels?.[0] ||
      (input.id ? shadow.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);

    if (label && input.id && label.htmlFor !== input.id) {
      label.htmlFor = input.id;
    }

    if (input.hasAttribute('aria-labelledby')) {
      input.removeAttribute('aria-labelledby');
    }

    // Native <label for> association is sufficient when it works; avoid duplicate aria-label.
    const hasNativeLabel = Boolean(label && input.labels?.length);
    if (hasNativeLabel && input.hasAttribute('aria-label') && input.dataset.a11yAriaLabel !== 'true') {
      input.removeAttribute('aria-label');
    } else if (!hasNativeLabel) {
      const labelText = label?.textContent?.replace(/\s+/g, ' ').trim();
      if (labelText) {
        input.setAttribute('aria-label', labelText);
        input.dataset.a11yAriaLabel = 'true';
      }
    }
  });
};

const SHOPIFY_FORM_ERROR_OUTPUT_SELECTOR = 'output[class*="_formError_"], output[class*="_error_"]';

const getShopifyFormInputForErrorOutput = (output) => {
  const previousSibling = output.previousElementSibling;
  if (previousSibling?.matches?.('[class*="formFieldContainer"]')) {
    const input = previousSibling.querySelector('input:not([type="hidden"]), select, textarea');
    if (input) return input;
  }

  const fieldContainer =
    output.closest('[class*="formFieldContainer"]') || output.closest('[class*="formFileInput"]');
  return fieldContainer?.querySelector('input:not([type="hidden"]), select, textarea') || null;
};

const getShopifyFormErrorIdForInput = (inputId) => `a11y-shopify-form-error-${inputId.replace(/#/g, '-')}`;

const SHOPIFY_FORM_GENERIC_INVALID_PATTERN = /\bis invalid\b/i;

const isShopifyFormEmailField = (input, shadow) => {
  if (input.type === 'email') return true;
  const labelText = getShopifyFormFieldLabelText(input, shadow).toLowerCase();
  return /e-?mail/.test(labelText);
};

const isShopifyFormStateProvinceField = (input, shadow) => {
  const labelText = getShopifyFormFieldLabelText(input, shadow).toLowerCase();
  const combined = `${labelText} ${input.name || ''} ${input.id || ''}`.toLowerCase();
  return /\bstate\b|province|region/.test(combined);
};

const getShopifyFormStateProvinceFieldLabel = (labelText) => {
  if (/\bprovince\b/i.test(labelText)) return 'Province';
  if (/\bregion\b/i.test(labelText)) return 'Region';
  return 'State/Province';
};

const getShopifyFormDetailedFormatErrorMessage = (input, shadow) => {
  if (isShopifyFormEmailField(input, shadow)) {
    return 'Error: Email is invalid. Please use the example@domain.com format';
  }

  if (isShopifyFormStateProvinceField(input, shadow)) {
    const labelText = getShopifyFormFieldLabelText(input, shadow);
    const fieldName = getShopifyFormStateProvinceFieldLabel(labelText);

    if (input.tagName === 'SELECT') {
      return `Error: ${fieldName} is invalid. Please select a valid option from the list`;
    }

    return `Error: ${fieldName} is invalid. Please enter a valid state or province (for example, CA or California)`;
  }

  return null;
};

const enhanceShopifyFormValidationErrorMessage = (input, errorText, shadow) => {
  if (!errorText || !SHOPIFY_FORM_GENERIC_INVALID_PATTERN.test(errorText)) {
    return errorText;
  }

  if (/please use the|please select a valid|please enter a valid/i.test(errorText)) {
    return errorText;
  }

  return getShopifyFormDetailedFormatErrorMessage(input, shadow) || errorText;
};

const isShopifyFormEmailValueValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

const isShopifyFormStateProvinceValueValid = (input, value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return false;

  if (input.tagName === 'SELECT') {
    const option = input.selectedOptions?.[0];
    return Boolean(option && !option.disabled && trimmed);
  }

  return trimmed.length >= 2;
};

const getShopifyFormErrorOutputForInput = (input, shadow) => {
  const errorId = getShopifyFormErrorIdForInput(input.id);
  const byId = shadow.getElementById(errorId);
  if (byId) return byId;

  const container = input.closest('[class*="formFieldContainer"]');
  return container?.querySelector(SHOPIFY_FORM_ERROR_OUTPUT_SELECTOR) || null;
};

const syncShopifyFormFormatFieldError = (input, shadow) => {
  const detailedMessage = getShopifyFormDetailedFormatErrorMessage(input, shadow);
  if (!detailedMessage) return;

  const valid = isShopifyFormEmailField(input, shadow)
    ? isShopifyFormEmailValueValid(input.value)
    : isShopifyFormStateProvinceValueValid(input, input.value);

  if (valid) {
    delete input.dataset.a11yFormatErrorActive;

    const output = getShopifyFormErrorOutputForInput(input, shadow);
    if (output?.textContent?.trim()) {
      output.textContent = '';
      fixShopifyFormFieldErrors(shadow);
    }

    return;
  }

  if (input.dataset.a11yFormatErrorActive !== 'true') return;

  const output = getShopifyFormErrorOutputForInput(input, shadow);
  if (!output) return;

  const current = output.textContent?.replace(/\s+/g, ' ').trim();
  if (current !== detailedMessage) {
    output.textContent = detailedMessage;
  }

  fixShopifyFormFieldErrors(shadow);
};

const setupShopifyFormFormatFieldErrorPersistence = (input, shadow) => {
  if (input.dataset.a11yFormatErrorListener === 'true') return;
  if (!getShopifyFormDetailedFormatErrorMessage(input, shadow)) return;

  input.dataset.a11yFormatErrorListener = 'true';

  const handler = () => syncShopifyFormFormatFieldError(input, shadow);
  input.addEventListener('input', handler);
  input.addEventListener('change', handler);
  input.addEventListener('blur', handler);
};

const getShopifyFormFieldLabelText = (input, shadow) => {
  const label =
    input.labels?.[0] || (input.id ? shadow.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);

  const labelText = label?.textContent?.replace(/\s+/g, ' ').trim();
  if (labelText) return labelText;

  const labelledBy = input.getAttribute('aria-labelledby');
  if (labelledBy) {
    return labelledBy
      .split(/\s+/)
      .map((id) => shadow.getElementById(id)?.textContent?.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join(' ');
  }

  return '';
};

const inferShopifyFormAutocomplete = (input, labelText) => {
  const combined = `${labelText} ${input.name || ''} ${input.id || ''} ${input.placeholder || ''}`.toLowerCase();

  if (input.type === 'email') return 'email';
  if (input.type === 'tel') return 'tel';
  if (input.type === 'url') return 'url';
  if (input.type === 'password') return 'new-password';

  const rules = [
    [/e-?mail/, 'email'],
    [/phone|mobile|cell|telephone|fax/, 'tel'],
    [/first[\s_-]*name|given[\s_-]*name/, 'given-name'],
    [/last[\s_-]*name|family[\s_-]*name|surname/, 'family-name'],
    [/company|organization|organisation|business\s*name|employer/, 'organization'],
    [/address\s*line\s*2|apt\.?|suite|unit\s*#?/, 'address-line2'],
    [/\bstreet\b|address\s*line\s*1/, 'address-line1'],
    [/\baddress\b/, 'street-address'],
    [/\bcity\b|town/, 'address-level2'],
    [/\bstate\b|province|region/, 'address-level1'],
    [/zip|postal/, 'postal-code'],
    [/\bcountry\b/, 'country'],
    [/full\s*name|contact\s*name|\bname\b/, 'name'],
  ];

  for (const [pattern, token] of rules) {
    if (pattern.test(combined)) return token;
  }

  if (input.tagName === 'SELECT' && /\bcountry\b/.test(combined)) return 'country';
  if (input.tagName === 'SELECT' && /\bstate\b|province/.test(combined)) return 'address-level1';

  return null;
};

const shouldReplaceShopifyFormAutocomplete = (existing, inferredToken) => {
  const normalized = (existing || '').trim().toLowerCase();
  if (!normalized) return true;
  // Shopify Forms defaults every field to off; replace when we can map a W3C input purpose.
  if (normalized === 'off' && inferredToken) return true;
  return false;
};

// FG-RTEP-04: Common form fields must expose a valid autocomplete token (W3C input purposes).
const fixShopifyFormAutocomplete = (shadow) => {
  shadow
    .querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), select, textarea')
    .forEach((input) => {
      const existing = input.getAttribute('autocomplete');
      const labelText = getShopifyFormFieldLabelText(input, shadow);
      const token = inferShopifyFormAutocomplete(input, labelText);

      if (shouldReplaceShopifyFormAutocomplete(existing, token)) {
        input.setAttribute('autocomplete', token || 'off');
        return;
      }

      if (!existing || existing.trim() === '') {
        input.setAttribute('autocomplete', token || 'off');
      }
    });
};

// FG-RTEP-06: Associate inline validation errors with inputs via aria-describedby.
const fixShopifyFormFieldErrors = (shadow) => {
  shadow.querySelectorAll(SHOPIFY_FORM_ERROR_OUTPUT_SELECTOR).forEach((output) => {
    const input = getShopifyFormInputForErrorOutput(output);
    if (!input?.id) return;

    const errorId = getShopifyFormErrorIdForInput(input.id);
    let errorText = output.textContent?.replace(/\s+/g, ' ').trim();

    if (errorText) {
      const enhanced = enhanceShopifyFormValidationErrorMessage(input, errorText, shadow);
      if (enhanced !== errorText) {
        output.textContent = enhanced;
        errorText = enhanced;
      }

      if (getShopifyFormDetailedFormatErrorMessage(input, shadow)) {
        input.dataset.a11yFormatErrorActive = 'true';
      }
    }

    if (!errorText) {
      if (output.id === errorId) {
        output.removeAttribute('id');
      }

      const describedBy = (input.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== errorId);

      if (describedBy.length) {
        input.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        input.removeAttribute('aria-describedby');
      }

      if (input.getAttribute('aria-invalid') === 'true') {
        input.setAttribute('aria-invalid', 'false');
      }

      output.removeAttribute('data-a11y-error-linked');
      return;
    }

    if (output.id !== errorId) {
      output.id = errorId;
    }
    output.setAttribute('data-a11y-error-linked', 'true');

    const describedBy = (input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean);

    if (!describedBy.includes(errorId)) {
      describedBy.push(errorId);
      input.setAttribute('aria-describedby', describedBy.join(' '));
    }

    if (input.getAttribute('aria-invalid') !== 'true') {
      input.setAttribute('aria-invalid', 'true');
    }
  });
};

// FG-RTEP-07: Replace vague "is invalid" copy with format-specific guidance until the value is corrected.
const fixShopifyFormFormatValidationErrors = (shadow) => {
  shadow
    .querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), select, textarea')
    .forEach((input) => {
      setupShopifyFormFormatFieldErrorPersistence(input, shadow);
      syncShopifyFormFormatFieldError(input, shadow);
    });
};

// FG-RTEP-11: Shopify Forms input borders use #D2D2D2 (~1.5:1 on white); use #949494 (~3:1).
const fixShopifyFormFieldBorderContrast = (shadow) => {
  if (shadow.querySelector('[data-a11y-form-field-border-styles]')) return;

  const hasFields = shadow.querySelector('input:not([type="hidden"]), select, textarea');
  if (!hasFields) return;

  const style = document.createElement('style');
  style.setAttribute('data-a11y-form-field-border-styles', 'true');
  style.textContent = `
    input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]),
    select,
    textarea {
      border-color: #949494 !important;
    }
  `;
  shadow.appendChild(style);
};

// FG-RTEP-09: Shopify Forms floating labels (#8F8F8F @ 3.23:1); solid #757575 @ 4.6:1 on white (no opacity).
const fixShopifyFormFieldLabelContrast = (shadow) => {
  if (shadow.querySelector('[data-a11y-form-field-label-styles]')) return;

  const hasLabels = shadow.querySelector(
    'label[class*="InputFieldLabel"], label[class*="formFieldLabel"], [class*="InputFieldLabel"]'
  );
  if (!hasLabels) return;

  const style = document.createElement('style');
  style.setAttribute('data-a11y-form-field-label-styles', 'true');
  style.textContent = `
    label[class*="InputFieldLabel"],
    label[class*="formFieldLabel"],
    [class*="InputFieldLabel"]:not(input):not(textarea):not(select) {
      color: #757575 !important;
      opacity: 1 !important;
    }
  `;
  shadow.appendChild(style);
};

const SHOPIFY_FORM_SUBMIT_SELECTORS = 'button[type="submit"], input[type="submit"]';

const getShopifyFormSubmitDefaultColors = (submit) => {
  const clone = submit.cloneNode(true);
  clone.tabIndex = -1;
  clone.setAttribute('aria-hidden', 'true');
  clone.style.cssText =
    'position:absolute !important;left:-9999px !important;width:1px !important;height:1px !important;opacity:0 !important;pointer-events:none !important;';
  const parent = submit.parentElement;
  if (!parent) return null;

  parent.appendChild(clone);
  const computed = getComputedStyle(clone);
  const colors = {
    backgroundColor: computed.backgroundColor,
    borderColor: computed.borderColor,
    color: computed.color,
  };
  clone.remove();
  return colors;
};

const getShopifyFormSubmitColorsFromHost = (shadow, fallbackColors) => {
  const hostStyle = shadow?.host ? getComputedStyle(shadow.host) : null;
  const backgroundColor =
    hostStyle?.getPropertyValue('--button-background-color')?.trim() || fallbackColors?.backgroundColor;
  const color = hostStyle?.getPropertyValue('--button-text-color')?.trim() || fallbackColors?.color;
  const borderColor = backgroundColor || fallbackColors?.borderColor;

  return { backgroundColor, borderColor, color };
};

const applyShopifyFormSubmitFocusColors = (submit, colors, shadow) => {
  const resolved = getShopifyFormSubmitColorsFromHost(shadow, colors);
  if (!resolved.backgroundColor || resolved.backgroundColor === 'rgba(0, 0, 0, 0)') return;

  submit.style.setProperty('background-color', resolved.backgroundColor, 'important');
  submit.style.setProperty('border-color', resolved.borderColor, 'important');
  submit.style.setProperty('color', resolved.color, 'important');
  submit.style.setProperty('box-shadow', 'none', 'important');
  submit.style.setProperty('background-image', 'none', 'important');
  submit.style.setProperty('filter', 'none', 'important');
  submit.style.setProperty('opacity', '1', 'important');
};

const bindShopifyFormSubmitFocusColorLock = (submit, colors, shadow) => {
  if (submit.dataset.a11ySubmitFocusLock === 'true') return;
  submit.dataset.a11ySubmitFocusLock = 'true';

  const lockColors = () => {
    applyShopifyFormSubmitFocusColors(submit, colors, shadow);
    requestAnimationFrame(() => applyShopifyFormSubmitFocusColors(submit, colors, shadow));
  };

  submit.addEventListener('focus', lockColors);
  submit.addEventListener('focusin', lockColors);

  const styleObserver = new MutationObserver(() => {
    if (submit.matches(':focus')) {
      lockColors();
    }
  });
  styleObserver.observe(submit, { attributes: true, attributeFilter: ['style', 'class'] });
};

// FG-RTEP-10: Shopify Forms submit uses #4091C5 on focus (~3.45:1 on white text); keep default colors.
const fixShopifyFormSubmitButtonFocus = (shadow) => {
  const submits = shadow.querySelectorAll(SHOPIFY_FORM_SUBMIT_SELECTORS);
  if (!submits.length) return;

  const referenceColors = getShopifyFormSubmitDefaultColors(submits[0]);
  if (!referenceColors?.backgroundColor || referenceColors.backgroundColor === 'rgba(0, 0, 0, 0)') {
    return;
  }

  if (!shadow.querySelector('[data-a11y-form-submit-focus-styles]')) {
    const { backgroundColor, borderColor, color } = referenceColors;
    const style = document.createElement('style');
    style.setAttribute('data-a11y-form-submit-focus-styles', 'true');
    style.textContent = `
      ${SHOPIFY_FORM_SUBMIT_SELECTORS}:focus,
      ${SHOPIFY_FORM_SUBMIT_SELECTORS}:focus-visible {
        background-color: var(--button-background-color, ${backgroundColor}) !important;
        background-image: none !important;
        border-color: var(--button-background-color, ${borderColor}) !important;
        color: var(--button-text-color, ${color}) !important;
        box-shadow: none !important;
        filter: none !important;
        opacity: 1 !important;
      }

      ${SHOPIFY_FORM_SUBMIT_SELECTORS}:focus-visible {
        outline: 0.2rem solid var(--forms-accent-color, #0076bb);
        outline-offset: 0.2rem;
      }
    `;
    shadow.appendChild(style);
  }

  submits.forEach((submit) => {
    const colors = getShopifyFormSubmitDefaultColors(submit) || referenceColors;
    bindShopifyFormSubmitFocusColorLock(submit, colors, shadow);
  });
};

const SHOPIFY_FORM_SUCCESS_BANNER_SELECTOR =
  '[data-testid="banner-success"], section[class*="_formBannerContainer_"]';

const getShopifyFormSuccessMessageText = (banner) => {
  const heading = banner.querySelector('h2')?.textContent?.replace(/\s+/g, ' ').trim() || '';
  const body =
    banner.querySelector('[class*="_textBody_"]')?.textContent?.replace(/\s+/g, ' ').trim() || '';
  return [heading, body].filter(Boolean).join('. ');
};

// FG-RTEP-08: After submit, inline Shopify Forms show a success message without notifying AT users.
const fixShopifyFormSubmitSuccessAnnouncement = (shadow) => {
  const appEmbed = shadow.getElementById('app-embed');
  const currentStep = appEmbed?.getAttribute('data-current-step');

  if (currentStep !== 'success') {
    delete shadow.dataset.a11ySubmitSuccessAnnounced;
    return;
  }

  const banner = shadow.querySelector(SHOPIFY_FORM_SUCCESS_BANNER_SELECTOR);
  if (!banner) return;

  if (!banner.getAttribute('role')) {
    banner.setAttribute('role', 'status');
  }
  if (!banner.getAttribute('aria-live')) {
    banner.setAttribute('aria-live', 'polite');
  }
  banner.setAttribute('aria-atomic', 'true');

  const focusTarget = banner.querySelector('h2') || banner;
  if (!focusTarget.hasAttribute('tabindex')) {
    focusTarget.setAttribute('tabindex', '-1');
  }

  const messageText = getShopifyFormSuccessMessageText(banner);
  const announcementKey = messageText || 'success';
  if (shadow.dataset.a11ySubmitSuccessAnnounced === announcementKey) return;

  shadow.dataset.a11ySubmitSuccessAnnounced = announcementKey;

  let liveRegion = shadow.querySelector('[data-a11y-form-submit-success]');
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('data-a11y-form-submit-success', 'true');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    Object.assign(liveRegion.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: '0',
    });
    shadow.appendChild(liveRegion);
  }

  liveRegion.textContent = '';
  window.requestAnimationFrame(() => {
    if (messageText) {
      liveRegion.textContent = messageText;
    }
    focusTarget.focus({ preventScroll: false });
  });
};

const injectShopifyFormFileStyles = (shadow) => {
  if (shadow.querySelector('[data-a11y-form-file-styles]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-a11y-form-file-styles', 'true');
  style.textContent = `
    button[data-a11y-remove-file] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 0.8rem;
      margin-bottom: 8px;
      padding: 10px 16px;
      border: 1px solid #c62828;
      border-radius: 4px;
      background: transparent;
      color: #c62828;
      font-size: 12px;
      line-height: 1.2;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    button[data-a11y-remove-file]:hover,
    button[data-a11y-remove-file]:focus-visible {
      background: #c62828;
      color: #fff;
    }

    button[data-a11y-remove-file] svg {
      display: none;
    }

    [data-a11y-file-selected-status] {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `;
  shadow.appendChild(style);
};

const SHOPIFY_FORM_ADD_FILE_CONTROL_LABEL_PATTERN = /^add\s*file$/i;

const normalizeShopifyFormA11yText = (text) => text?.replace(/\s+/g, ' ').trim() || '';

const isShopifyFormFileFieldTitleElement = (element, fileField) => {
  if (!element || fileField?.contains(element)) return false;

  const text = normalizeShopifyFormA11yText(element.textContent);
  if (!text || SHOPIFY_FORM_ADD_FILE_CONTROL_LABEL_PATTERN.test(text)) return false;

  return true;
};

const getShopifyFormFileFieldLabelElement = (fileInput, fileInputContainer) => {
  const fileField =
    fileInput.closest('[class*="_formFileInputField"]') ||
    fileInput.closest('[class*="formFileInputField"]') ||
    fileInput.parentElement;

  const fileInputRoot =
    fileInputContainer.closest('[class*="formFileInput"]') ||
    fileInputContainer.parentElement ||
    fileInputContainer;

  const fieldContainer = fileInputRoot.closest('[class*="formFieldContainer"]');

  const titleSelectors = [
    'p[class*="textBody"]',
    'label[class*="InputFieldLabel"]',
    'label[class*="formFieldLabel"]',
    '[class*="formFieldLabel"]',
  ];

  for (const selector of titleSelectors) {
    for (const candidate of fileInputRoot.querySelectorAll(selector)) {
      if (isShopifyFormFileFieldTitleElement(candidate, fileField)) {
        return candidate;
      }
    }
  }

  if (fieldContainer) {
    const externalLabel = fieldContainer.querySelector(
      'label[class*="InputFieldLabel"], label[class*="formFieldLabel"]'
    );
    if (isShopifyFormFileFieldTitleElement(externalLabel, fileField)) {
      return externalLabel;
    }
  }

  for (const child of fileInputRoot.children) {
    if (child === fileField || fileField?.contains(child)) continue;
    if (isShopifyFormFileFieldTitleElement(child, fileField)) {
      return child;
    }
  }

  return null;
};

const getShopifyFormFileFieldDescriptionId = (inputId) =>
  `a11y-shopify-form-file-desc-${inputId.replace(/#/g, '-')}`;

// FG-RTEP-01: File control name must match visible "Add file"; field title via aria-describedby.
const mergeShopifyFormFileInputDescribedBy = (fileInput, descriptionElementId) => {
  const describedBy = (fileInput.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((id) => id && id !== descriptionElementId);

  describedBy.push(descriptionElementId);
  fileInput.setAttribute('aria-describedby', describedBy.join(' '));
};

const announceShopifyFormFileSelected = (fileInputContainer) => {
  let status = fileInputContainer.querySelector('[data-a11y-file-selected-status]');
  if (!status) {
    status = document.createElement('span');
    status.setAttribute('data-a11y-file-selected-status', 'true');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('role', 'status');
    fileInputContainer.appendChild(status);
  }

  status.textContent = '';
  window.requestAnimationFrame(() => {
    status.textContent = 'File selected.';
  });
};

const fixShopifyFormFileUpload = (shadow) => {
  const fileInput = shadow.querySelector('input[type="file"]');
  if (!fileInput || fileInput.dataset.a11yFileFixed === 'true') return;

  injectShopifyFormFileStyles(shadow);

  const fileField =
    fileInput.closest('[class*="_formFileInputField"]') ||
    fileInput.closest('[class*="formFileInputField"]') ||
    fileInput.parentElement;

  if (!fileField) return;

  const fileInputContainer = fileField.parentElement;
  if (!fileInputContainer) return;

  fileInput.dataset.a11yFileFixed = 'true';

  const getClearButton = () =>
    fileInputContainer.querySelector(
      'button[id$="-clear"], button[data-a11y-remove-file], button[aria-label*="Clear" i], button[aria-label*="Remove" i], [class*="CancelButton"]'
    ) ||
    shadow.querySelector(`button[id="${CSS.escape(fileInput.id.replace(/-field$/, '-clear'))}"]`);

  let applyingFileFixes = false;

  const applyFileUploadFixes = () => {
    if (applyingFileFixes) return;
    applyingFileFixes = true;

    try {
      const fileLabel = fileInput.closest('label');
      if (fileLabel?.contains(fileInput)) {
        fileLabel.parentElement?.insertBefore(fileInput, fileLabel);

        if (!fileLabel.hasAttribute('for') && fileInput.id) {
          fileLabel.setAttribute('for', fileInput.id);
        }

        fileLabel.setAttribute('aria-hidden', 'true');
        fileLabel.setAttribute('tabindex', '-1');
      }

      const addFileControl = fileField.querySelector('[class*="FileInputButton"]');
      if (addFileControl) {
        if (addFileControl.getAttribute('aria-hidden') === 'true') {
          addFileControl.removeAttribute('aria-hidden');
        }

        if (!addFileControl.id && fileInput.id) {
          addFileControl.id = `a11y-shopify-form-file-control-${fileInput.id.replace(/#/g, '-')}`;
        }

        if (addFileControl.id) {
          fileInput.setAttribute('aria-labelledby', addFileControl.id);
        }
      }

      fileField
        .querySelectorAll('button:not([data-a11y-remove-file]), [class*="FileInputButton"]')
        .forEach((control) => {
          if (control.id?.endsWith('-clear') || control.closest('[class*="CancelButton"]')) return;
          if (control.hasAttribute('aria-label')) {
            control.removeAttribute('aria-label');
          }
        });

      if (fileInput.hasAttribute('aria-label')) {
        fileInput.removeAttribute('aria-label');
      }
      delete fileInput.dataset.a11yAriaLabel;

      const fieldLabel = getShopifyFormFileFieldLabelElement(fileInput, fileInputContainer);
      if (fieldLabel && fileInput.id) {
        if (!fieldLabel.id) {
          fieldLabel.id = getShopifyFormFileFieldDescriptionId(fileInput.id);
        }
        mergeShopifyFormFileInputDescribedBy(fileInput, fieldLabel.id);
      }

      const clearButton = getClearButton();
      if (!clearButton) return;

      if (clearButton.parentElement !== fileInputContainer) {
        fileInputContainer.appendChild(clearButton);
      }

      if (!clearButton.hasAttribute('data-a11y-remove-file')) {
        clearButton.setAttribute('data-a11y-remove-file', 'true');
      }

      if (clearButton.getAttribute('aria-label') !== 'Remove uploaded file') {
        clearButton.setAttribute('aria-label', 'Remove uploaded file');
      }

      const icon = clearButton.querySelector('svg');
      if (icon && icon.getAttribute('aria-hidden') !== 'true') {
        icon.setAttribute('aria-hidden', 'true');
      }

      let removeText = clearButton.querySelector('[data-a11y-remove-text]');
      if (!removeText) {
        removeText = document.createElement('span');
        removeText.setAttribute('data-a11y-remove-text', 'true');
        clearButton.appendChild(removeText);
      }

      if (removeText.textContent !== 'Remove') {
        removeText.textContent = 'Remove';
      }
    } finally {
      applyingFileFixes = false;
    }
  };

  let debounceId;
  const scheduleFileUploadFixes = () => {
    window.clearTimeout(debounceId);
    debounceId = window.setTimeout(applyFileUploadFixes, 100);
  };

  fileInput.addEventListener('change', () => {
    scheduleFileUploadFixes();
    if (fileInput.files?.length) {
      announceShopifyFormFileSelected(fileInputContainer);
    }
  });

  if (!fileInputContainer.dataset.a11yRemoveListener) {
    fileInputContainer.dataset.a11yRemoveListener = 'true';
    fileInputContainer.addEventListener('click', (event) => {
      if (event.target.closest('button[id$="-clear"], button[data-a11y-remove-file], [class*="CancelButton"]')) {
        scheduleFileUploadFixes();
      }
    });
  }

  const fileObserver = new MutationObserver((mutations) => {
    const isRelevant = mutations.some((mutation) => {
      if (mutation.type !== 'childList') return false;
      return Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('button[id$="-clear"], [class*="CancelButton"], [class*="FileName"]') ||
            node.querySelector?.('button[id$="-clear"], [class*="CancelButton"], [class*="FileName"]'))
      );
    });

    if (isRelevant) scheduleFileUploadFixes();
  });

  fileObserver.observe(fileInputContainer, { childList: true, subtree: true });

  applyFileUploadFixes();
};

const initSpecificationTableHeaders = () => {
  document.querySelectorAll('.accordion__content table').forEach((table) => {
    if (table.dataset.a11yRowHeaders === 'true') return;
    table.dataset.a11yRowHeaders = 'true';

    table.querySelectorAll('tr').forEach((row) => {
      if (row.querySelector('th')) return;

      const cells = row.querySelectorAll('td');
      if (cells.length !== 2) return;

      const headerCell = cells[0];
      const th = document.createElement('th');
      th.scope = 'row';
      th.innerHTML = headerCell.innerHTML;
      headerCell.replaceWith(th);
    });
  });
};

let judgeMeObserver;

const JUDGE_ME_SECONDARY_TEXT_COLOR = '#373737';

const fixJudgeMeContrastColors = () => {
  document
    .querySelectorAll(
      '#judgeme_product_reviews .jdgm-rev-widg__summary-text, #judgeme_product_reviews .jdgm-rev-widg__summary-average, #judgeme_product_reviews .jdgm-histogram__frequency, #judgeme_product_reviews .jdgm-rev__timestamp, #judgeme_product_reviews .jdgm-rev__location, .jdgm-rev-widg .jdgm-rev-widg__summary-text, .jdgm-rev-widg .jdgm-rev-widg__summary-average, .jdgm-rev-widg .jdgm-histogram__frequency, .jdgm-rev-widg .jdgm-rev__timestamp, .jdgm-rev-widg .jdgm-rev__location'
    )
    .forEach((element) => {
      if (element.style.getPropertyValue('color') === JUDGE_ME_SECONDARY_TEXT_COLOR) return;
      element.style.setProperty('color', JUDGE_ME_SECONDARY_TEXT_COLOR, 'important');
    });
};

const initJudgeMeA11y = () => {
  fixJudgeMeStarLabels();
  fixJudgeMePagination();
  fixJudgeMeDecorativeIcons();
  fixJudgeMeContrastColors();

  if (judgeMeObserver) return;

  judgeMeObserver = new MutationObserver((mutations) => {
    const hasJudgeMeMutation = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      if (target?.closest?.('.jdgm-widget, #judgeme_product_reviews')) return true;

      return Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.jdgm-widget, #judgeme_product_reviews') ||
            node.querySelector?.('.jdgm-widget, #judgeme_product_reviews'))
      );
    });

    if (!hasJudgeMeMutation) return;

    fixJudgeMeStarLabels();
    fixJudgeMePagination();
    fixJudgeMeDecorativeIcons();
    fixJudgeMeContrastColors();
  });
  judgeMeObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-label', 'data-average-rating'],
  });
};

const fixJudgeMeStarLabels = () => {
  document.querySelectorAll('.jdgm-prev-badge, .jdgm-rev-widg__summary').forEach((badge) => {
    const stars =
      badge.querySelector('[role="img"]') ||
      badge.querySelector('.jdgm-rev-widg__summary-stars') ||
      badge.querySelector('.jdgm-star-all');
    if (!stars) return;

    const ratingSource = badge.closest('[data-average-rating]') || badge.querySelector('[data-average-rating]');
    const existingLabel = stars.getAttribute('aria-label') || '';
    const rating =
      ratingSource?.dataset?.averageRating ||
      ratingSource?.getAttribute('data-average-rating') ||
      existingLabel.match(/\d+(?:\.\d+)?/)?.[0];

    if (rating) {
      const label = `Average rating is ${rating} out of 5 stars`;
      if (stars.getAttribute('aria-label') === label) return;

      stars.setAttribute('aria-label', `Average rating is ${rating} out of 5 stars`);
    }
  });
};

const fixJudgeMePagination = () => {
  document.querySelectorAll('.jdgm-paginate').forEach((paginate) => {
    if (paginate.querySelector('nav.jdgm-paginate-nav')) return;

    const pageLinks = Array.from(paginate.querySelectorAll('a, button, span.jdgm-paginate__page'));
    if (!pageLinks.length) return;

    const nav = document.createElement('nav');
    nav.className = 'jdgm-paginate-nav';
    nav.setAttribute('aria-label', 'reviews pagination');
    const list = document.createElement('ul');

    pageLinks.forEach((link) => {
      const item = document.createElement('li');
      if (
        link.classList.contains('jdgm-curt') ||
        link.classList.contains('jdgm-paginate__page--current') ||
        link.getAttribute('aria-current') === 'page'
      ) {
        link.setAttribute('aria-current', 'page');
      }
      item.appendChild(link);
      list.appendChild(item);
    });

    nav.appendChild(list);
    paginate.innerHTML = '';
    paginate.appendChild(nav);
  });
};

const fixJudgeMeDecorativeIcons = () => {
  document.querySelectorAll('.jdgm-rev__author-wrapper .jdgm-rev__author-badge, .jdgm-rev__buyer-badge').forEach((icon) => {
    icon.setAttribute('aria-hidden', 'true');
  });
};

const HORIZONTAL_CONTENT_TABS_MQ = '(min-width: 990px)';

const applyMobileHorizontalTabPanelSemantics = (panel) => {
  const summaryId = panel.dataset.a11yAccordionLabelledby;
  panel.setAttribute('role', 'region');
  if (summaryId) {
    panel.setAttribute('aria-labelledby', summaryId);
  }
  panel.removeAttribute('hidden');
  panel.removeAttribute('aria-hidden');
};

const getHorizontalTablistLabel = (tablist) => {
  if (tablist.dataset.a11yTablistLabel) {
    return tablist.dataset.a11yTablistLabel;
  }
  const label = tablist.getAttribute('aria-label')?.trim();
  if (label) {
    tablist.dataset.a11yTablistLabel = label;
  }
  return label || '';
};

let horizontalTabStatusRegion = null;

const ensureHorizontalTabStatusRegion = () => {
  if (horizontalTabStatusRegion?.isConnected) return horizontalTabStatusRegion;

  horizontalTabStatusRegion = document.createElement('div');
  horizontalTabStatusRegion.id = 'a11y-horizontal-tab-status';
  horizontalTabStatusRegion.className = 'visually-hidden';
  horizontalTabStatusRegion.setAttribute('role', 'status');
  horizontalTabStatusRegion.setAttribute('aria-live', 'polite');
  horizontalTabStatusRegion.setAttribute('aria-atomic', 'true');
  document.body.appendChild(horizontalTabStatusRegion);
  return horizontalTabStatusRegion;
};

const announceHorizontalTabSelection = (tab) => {
  if (!tab || !window.matchMedia(HORIZONTAL_CONTENT_TABS_MQ).matches) return;

  const tablist = tab.closest('[role="tablist"]');
  if (!tablist) return;

  const tabs = [...tablist.querySelectorAll('[role="tab"]')];
  const index = tabs.indexOf(tab);
  if (index < 0) return;

  const name = tab.textContent?.replace(/\s+/g, ' ').trim() || '';
  const status = ensureHorizontalTabStatusRegion();
  status.textContent = `${name}, tab ${index + 1} of ${tabs.length}, selected`;
};

const syncHorizontalContentTabs = (root) => {
  const containers = root
    ? [root]
    : document.querySelectorAll('.collapsible-content-container.horizontal-tabs.one-rule');
  const isDesktop = window.matchMedia(HORIZONTAL_CONTENT_TABS_MQ).matches;

  containers.forEach((container) => {
    const tablist = container.querySelector('.collapsible-toggles-container');
    const tabs = tablist ? [...tablist.querySelectorAll('.collapsible-desktop-toggle')] : [];
    const panels = [...container.querySelectorAll('.accordion__content')];

    if (!tablist || !tabs.length) return;

    const tablistLabel = getHorizontalTablistLabel(tablist);

    if (!isDesktop) {
      tablist.removeAttribute('role');
      tablist.removeAttribute('aria-label');
      tabs.forEach((tab) => {
        tab.removeAttribute('role');
        tab.removeAttribute('aria-selected');
        tab.removeAttribute('aria-controls');
        tab.removeAttribute('tabindex');
      });
      panels.forEach(applyMobileHorizontalTabPanelSemantics);
      return;
    }

    tablist.setAttribute('role', 'tablist');
    if (tablistLabel) {
      tablist.setAttribute('aria-label', tablistLabel);
    }

    tabs.forEach((tab) => {
      const selected = tab.classList.contains('is-open');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel && tab.id) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        if (!panel.hasAttribute('tabindex')) {
          panel.setAttribute('tabindex', '-1');
        }
        if (selected) {
          panel.removeAttribute('hidden');
          panel.setAttribute('aria-hidden', 'false');
        } else {
          panel.setAttribute('hidden', '');
          panel.setAttribute('aria-hidden', 'true');
        }
      }
    });
  });
};

const focusHorizontalTabPanel = (container, tab) => {
  if (!window.matchMedia(HORIZONTAL_CONTENT_TABS_MQ).matches) return;
  const panelId = tab?.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel || !container?.contains(panel)) return;

  const focusTarget =
    panel.querySelector('[id^="SectionHeading-"]') ||
    panel.querySelector('.blog__title, .tab-title, h2, h3') ||
    panel;

  if (!focusTarget || typeof focusTarget.focus !== 'function') return;
  if (!focusTarget.hasAttribute('tabindex')) {
    focusTarget.setAttribute('tabindex', '-1');
  }
  focusTarget.focus({ preventScroll: true });
};

let horizontalContentTabsMqBound = false;

const initHorizontalContentTabsA11y = () => {
  syncHorizontalContentTabs();

  if (!horizontalContentTabsMqBound) {
    horizontalContentTabsMqBound = true;
    window
      .matchMedia(HORIZONTAL_CONTENT_TABS_MQ)
      .addEventListener('change', () => syncHorizontalContentTabs());
  }

  document.querySelectorAll('.horizontal-tabs.one-rule .collapsible-toggles-container').forEach((tablist) => {
    if (tablist.dataset.a11yTabsKeyboard === 'true') return;
    tablist.dataset.a11yTabsKeyboard = 'true';

    tablist.addEventListener('keydown', (event) => {
      if (!window.matchMedia(HORIZONTAL_CONTENT_TABS_MQ).matches) return;

      const tabs = [...tablist.querySelectorAll('[role="tab"]')];
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      tabs[nextIndex].click();
      tabs[nextIndex].focus();
    });
  });
};

window.syncHorizontalContentTabs = syncHorizontalContentTabs;
window.focusHorizontalTabPanel = focusHorizontalTabPanel;
window.announceHorizontalTabSelection = announceHorizontalTabSelection;

const initProductScrollPadding = () => {
  const main = document.getElementById('MainContent');
  if (!main || main.dataset.template !== 'product') return;

  const updateScrollPadding = () => {
    const quickOrderTotal = document.querySelector('.quick-order-list__total');
    const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    const totalHeight = quickOrderTotal ? quickOrderTotal.offsetHeight : 0;
    const headerHeight = header ? header.offsetHeight : 0;
    const padding = totalHeight + headerHeight + 16;

    document.documentElement.style.setProperty('--scroll-padding-bottom', `${padding}px`);
    document.documentElement.style.scrollPaddingBottom = `${padding}px`;
  };

  updateScrollPadding();
  window.addEventListener('resize', updateScrollPadding);

  const quickOrderList = document.querySelector('quick-order-list');
  if (quickOrderList) {
    const observer = new MutationObserver(updateScrollPadding);
    observer.observe(quickOrderList, { childList: true, subtree: true, attributes: true });
  }
};

const initAccessibility = () => {
  initTaxExemptionFormA11y();
  initSpecificationTableHeaders();
  initJudgeMeA11y();
  initHorizontalContentTabsA11y();
  initProductScrollPadding();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccessibility);
} else {
  initAccessibility();
}

document.addEventListener('shopify:section:load', initAccessibility);
